import React from 'react';
import { cn } from '@crisellevaldez/speculo-ui';
import { HiChat, HiMail, HiPhone } from 'react-icons/hi';
import { Tooltip } from '../../../common/Tooltip';
import { formatOptInDate } from '../../../../utils/dateTime';
import { mapContactConsent } from './mapContactConsent';
import { useFlywheelEnabled } from '../../../../stores/useOrganizationStore';
import { CONSENT_DATE_LABEL, CONSENT_STATUS_LABEL } from './consentLabels';
import type { ChannelConsentSummary, ConsentChannel, ConsentStatus } from './consent.types';
import type { GQLContactConsent } from '../../../../hooks/graphql/contacts/useContact';

const CHANNEL_META: Record<ConsentChannel, { label: string; icon: React.ReactNode }> = {
  phone: { label: 'Calls', icon: <HiPhone className="w-3.5 h-3.5" /> },
  email: { label: 'Email', icon: <HiMail className="w-3.5 h-3.5" /> },
  sms: { label: 'SMS', icon: <HiChat className="w-3.5 h-3.5" /> },
};

const STATUS_TONE: Record<ConsentStatus, string> = {
  opted_in: 'text-green-600 dark:text-green-400',
  // Any not-opted-in standing reads neutral gray; green is reserved for consent.
  opted_out: 'text-muted-foreground',
  dnc: 'text-muted-foreground',
  unknown: 'text-muted-foreground/40',
};

function tooltipFor(summary: ChannelConsentSummary): string {
  const { label } = CHANNEL_META[summary.channel];
  const dateLabel = CONSENT_DATE_LABEL[summary.status];
  const on = formatOptInDate(summary.changedAt);
  const when = dateLabel && on ? ` · ${dateLabel} ${on}` : '';
  return `${label} · ${CONSENT_STATUS_LABEL[summary.status]}${when}`;
}

/**
 * Per-channel consent as three tinted channel icons — the list-view counterpart
 * to the Consent tab's cards. Reads the same mapper, so a row and the tab can
 * never disagree.
 */
export const ChannelConsentIcons: React.FC<{
  consent?: GQLContactConsent | null;
  className?: string;
}> = ({ consent, className }) => {
  // SMS is a flywheel-only channel — an org that cannot send it gets no SMS
  // icon here, matching the Consent tab's cards.
  const flywheelEnabled = useFlywheelEnabled();
  const all = mapContactConsent(consent);
  const channels = flywheelEnabled ? all : all.filter((c) => c.channel !== 'sms');
  if (channels.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      {channels.map((summary) => (
        <Tooltip key={summary.channel} content={tooltipFor(summary)} position="top">
          <span className={cn('inline-flex cursor-help', STATUS_TONE[summary.status])}>
            {CHANNEL_META[summary.channel].icon}
          </span>
        </Tooltip>
      ))}
    </div>
  );
};
